<template>
  <div
    tabindex="0"
    class="dropdown"
    @keydown.up.prevent="up"
    @keydown.down.prevent="down"
    @keydown.enter.prevent="select"
    @blur.prevent="deactivate"
    @keyup.esc.prevent="deactivate"
  >
    <div
      class="dropdown-toggle d-flex flex-wrap align-items-center"
      @mousedown.prevent="toggle"
      :class="formClass"
    >
      <span v-if="max === 1">
        <span :style="{ display: value ? 'none' : 'inline' }">
          {{ title }}
        </span>
        <span v-if="value">
          <slot :value="value" :selector="true">
            {{ value.name ? value.name : value.id }}
          </slot>
        </span>
      </span>
      <span v-else>
        <span v-if="!value.length">
          {{ title }}
        </span>
        <span :key="item.id" v-for="item in value" class="item">
          {{ getName(item) }}
        </span>
      </span>
    </div>
    <ul
      class="dropdown-menu"
      :class="{ right: align === 'right' }"
      v-show="activation"
      ref="options"
    >
      <li
        :tabindex="!disabled() || selected(option) ? -1 : null"
        :key="option[source]"
        :id="option[source]"
        :index="index"
        :selected="selected(option)"
        :disabled="disabled()"
        class="d-flex justify-content-between align-items-center"
        @focus.prevent="focus"
        @blur.prevent="deactivate"
        @keyup.esc.prevent="
          deactivate();
          $el.focus();
        "
        @mouseenter.prevent="hover(index)"
        @click.stop.prevent="select"
        v-for="(option, index) in options"
      >
        <slot :value="option">{{ option.name }}</slot>
        <slot
          name="checker"
          :selectionIndex="getSelectionIndex(option)"
          v-if="selected(option)"
        >
          <span>✓</span>
        </slot>
      </li>
    </ul>
  </div>
</template>

<script>
export default {
  props: ["title", "value", "source", "options", "max", "formClass", "align"],
  data() {
    return {
      activation: false,
      focused: null,
      focusedIndex: -1
    };
  },
  watch: {
    activation(val) {
      if (val) {
        return;
      }

      this.focused = null;
      this.focusedIndex = -1;
    }
  },
  methods: {
    toggle() {
      this.activation = !this.activation;
      this.$el.focus();
    },
    deactivate() {
      this.activation = false;
    },
    focus(event) {
      const target = event.target;
      const index = target.getAttribute("index");
      this.focused = this.options[index];
      this.focusedIndex = index;
      this.activation = true;
    },
    hover(index, direction) {
      if (!this.activation) {
        this.activation = true;
        return;
      }

      let menu = null;
      if (!direction) {
        menu = this.$refs.options.children[index];
      } else {
        const focusableMenus = [].slice.call(
          this.$refs.options.querySelectorAll("[tabindex]")
        );

        if (direction === -1) {
          focusableMenus.reverse();
        }

        menu = focusableMenus.find(menu => {
          const menuIndex = parseInt(menu.getAttribute("index"));

          if (direction === 1) {
            return index < menuIndex;
          } else if (direction === -1) {
            return index > menuIndex;
          }
        });

        if (!menu) {
          menu = focusableMenus[0];
        }
      }

      menu.focus();
    },
    up() {
      this.hover(this.focusedIndex, -1);
    },
    down() {
      this.hover(this.focusedIndex, 1);
    },
    select() {
      if (!this.activation) {
        this.$emit("submit");
        return;
      }

      if (!this.focused) {
        this.activation = false;
        return;
      }

      this.check(this.focused);
    },
    getSelectionIndex(val) {
      if (this.max === 1) {
        if (!this.value) {
          return -1;
        }

        return this.value[this.source] === val[this.source] ? 0 : -1;
      }

      return this.value.findIndex(v => v[this.source] === val[this.source]);
    },
    selected(val) {
      return this.getSelectionIndex(val) !== -1;
    },
    disabled() {
      if (this.max === 1) {
        return false;
      }

      return this.value.length >= this.max;
    },
    check(selected) {
      if (this.max === 1) {
        this.activation = false;
        if (this.value && this.value[this.source] === selected[this.source]) {
          this.$emit("input", null);
          return;
        }

        this.$emit("input", selected);
        return;
      }

      const index = this.value.findIndex(v => {
        return v[this.source] === selected[this.source];
      });

      if (index !== -1) {
        this.value.splice(index, 1);
      } else {
        if (this.value.length >= this.max) {
          return;
        }

        this.value.push(selected);
      }
    },
    getName(item) {
      const option = this.options.find(
        option => option[this.source] === item[this.source]
      );
      return option ? option.name : option[this.source];
    }
  }
};
</script>

<style lang="scss" scoped>
.dropdown {
  min-width: 100px;
}

.dropdown-toggle {
  cursor: pointer;
  &::after {
    position: absolute;
    right: 0;
  }

  .item {
    margin-right: 2px;
    margin-bottom: 2px;
    border: 1px solid #eaeaea;
    border-radius: 10px;
    padding: 2px 5px;
  }
}

.dropdown:focus {
  outline-width: 0;
}

.dropdown-menu {
  display: flex;
  flex-flow: column wrap;
  padding: 8px;
  font-size: 13px;
  box-shadow: 0 4px 8px 0 rgba(0, 0, 0, 0.2), 0 6px 20px 0 rgba(0, 0, 0, 0.19);
}

.dropdown-menu.right {
  left: auto;
  right: 0;
}

.dropdown-menu li {
  padding: 2px 4px;
  border: 1px solid transparent;
  border-radius: 8px;
  list-style: none;
  cursor: pointer;
  margin-bottom: 1px;

  &[disabled] {
    background-color: transparent;
    color: #eaeaea;
    cursor: default;
  }

  &:focus {
    padding: 2px 12px;
    margin-left: -8px;
    margin-right: -8px;
    border-radius: 0;
  }

  &[selected] {
    background-color: #ededed;
    color: black;
    cursor: pointer;
  }

  &:focus,
  &[selected] {
    background-color: #ededed;
  }
}
</style>
